import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const VideoUploadContext = createContext(null);

export const useVideoUpload = () => {
  const context = useContext(VideoUploadContext);
  if (!context) {
    throw new Error('useVideoUpload must be used within a VideoUploadProvider');
  }
  return context;
};

export const VideoUploadProvider = ({ children }) => {
  const [activeUploads, setActiveUploads] = useState({});
  const xhrRefs = useRef({}); // id -> XMLHttpRequest en curso (para poder cancelar)

  // Guard against closing the browser
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasActive = Object.values(activeUploads).some(u => u.status === 'uploading');
      if (hasActive) {
        e.preventDefault();
        e.returnValue = 'Tienes una subida de vídeo en curso. Si cierras la pestaña se cancelará.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeUploads]);

  const startUpload = useCallback(async (file, rotation = 'AUTO', apiPrefix = '/api/canal-videos') => {
    const tempId = `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const token = localStorage.getItem('token');

    // Initial state
    setActiveUploads(prev => ({
      ...prev,
      [tempId]: {
        id: tempId,
        filename: file.name,
        progress: 0,
        status: 'initializing',
        error: null
      }
    }));

    try {
      // 1. Get Presigned URL
      const urlRes = await fetch(`${apiPrefix}/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'video/mp4',
          size: file.size
        })
      });

      if (!urlRes.ok) {
        const err = await urlRes.json();
        throw new Error(err.detail || 'Error al obtener URL firmada');
      }

      const { id: serverId, presigned_url } = await urlRes.json();

      // Update state with server ID
      setActiveUploads(prev => {
        const newState = { ...prev };
        delete newState[tempId];
        newState[serverId] = {
          id: serverId,
          filename: file.name,
          progress: 0,
          status: 'uploading',
          error: null
        };
        return newState;
      });

      // 2. Real Upload to S3
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRefs.current[serverId] = xhr; // referencia para poder cancelar
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setActiveUploads(prev => ({
              ...prev,
              [serverId]: { ...prev[serverId], progress: percent }
            }));
          }
        });

        xhr.addEventListener("load", () => {
          delete xhrRefs.current[serverId];
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Fallo S3: ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => { delete xhrRefs.current[serverId]; reject(new Error('Error de red')); });
        xhr.addEventListener("abort", () => { delete xhrRefs.current[serverId]; reject(new Error('Abortado')); });

        xhr.open("PUT", presigned_url);
        xhr.setRequestHeader("Content-Type", file.type || 'video/mp4');
        xhr.send(file);
      });

      // 3. Confirm to backend
      setActiveUploads(prev => ({
        ...prev,
        [serverId]: { ...prev[serverId], status: 'confirming', progress: 100 }
      }));

      const confirmRes = await fetch(`${apiPrefix}/confirm-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: serverId, rotation })
      });

      if (!confirmRes.ok) throw new Error('Error al confirmar');

      // Success - let the widget show completion for a few seconds then remove
      setActiveUploads(prev => ({
        ...prev,
        [serverId]: { ...prev[serverId], status: 'completed' }
      }));

      setTimeout(() => {
        setActiveUploads(prev => {
          const newState = { ...prev };
          delete newState[serverId];
          return newState;
        });
      }, 5000);

    } catch (error) {
      console.error("Video upload error:", error);
      const targetId = activeUploads[tempId] ? tempId : Object.keys(activeUploads).find(k => activeUploads[k].filename === file.name);

      // Cancelado por el usuario: quitar de la lista SIN marcar error ni avisar al backend.
      if (error.message === 'Abortado') {
        setActiveUploads(prev => { const s = { ...prev }; if (targetId) delete s[targetId]; return s; });
        return;
      }

      setActiveUploads(prev => ({
        ...prev,
        [targetId || tempId]: { 
          ...prev[targetId || tempId], 
          status: 'error', 
          error: error.message 
        }
      }));

      // Notify backend if possible
      const serverId = targetId && !targetId.startsWith('up_') ? targetId : null;
      if (serverId) {
        fetch(`${apiPrefix}/upload-error`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ id: serverId, message: error.message })
        }).catch(() => {});
      }
    }
  }, [activeUploads]);

  const removeUpload = useCallback((id) => {
    setActiveUploads(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  }, []);

  // Cancela una subida en curso: aborta el XHR (si lo hay) y la quita de la lista.
  const cancelUpload = useCallback((id) => {
    const xhr = xhrRefs.current[id];
    if (xhr) {
      try { xhr.abort(); } catch (e) { /* noop */ }
      // el handler 'abort' la quita de la lista
    } else {
      setActiveUploads(prev => { const s = { ...prev }; delete s[id]; return s; });
    }
  }, []);

  return (
    <VideoUploadContext.Provider value={{ activeUploads, startUpload, removeUpload, cancelUpload }}>
      {children}
    </VideoUploadContext.Provider>
  );
};
